import type { Payment as PaymentType } from "@/apis/gql/types";
import { ErrorMessage } from "@/components/ui/error";
import { LoadingSpinner } from "@/components/ui/spinner";
import type { BankAccountWithConnectionId } from "@/lib/payment/types";
import { useGetAllBankConnections } from "@/queries/bank-connections/get-all";
import { useEffect } from "react";
import { PaymentAccountSelectionComponent } from "./payment-account-selection.component";

type Props = {
  onConfirm: (bankAccountId: string, bankConnectionId: string) => void;
  onBack: () => void;
  payments: PaymentType[];
};

export const PaymentAccountSelection = ({
  onConfirm,
  onBack,
  payments,
}: Props) => {
  const { bankConnections, loading, error, refetch } = useGetAllBankConnections(
    {
      fetchPolicy: "network-only",
    },
  );

  useEffect(() => {
    if (payments.length === 0) {
      onBack();
    }
  }, [payments]);

  if (loading) {
    return (
      <div className="flex-1 flex justify-center items-center h-full">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex-1 flex justify-center items-center h-full">
        <ErrorMessage retry={refetch} />
      </div>
    );
  }

  const bankAccounts: BankAccountWithConnectionId[] = bankConnections.flatMap(
    (connection) =>
      connection.accounts.map((account) => ({
        ...account,
        bankConnectionId: connection.bankConnectionId,
        connectionName: connection.connectionName,
      })),
  );

  return (
    <PaymentAccountSelectionComponent
      bankAccounts={bankAccounts ?? []}
      onConfirm={onConfirm}
      onBack={onBack}
      totalPayments={payments.length}
    />
  );
};
